import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { apiErrorMessage } from '../api/client';
import * as inventoryApi from '../api/inventoryApi';
import * as ordersApi from '../api/ordersApi';
import * as statsApi from '../api/statsApi';
import { ClusterFormulationsModal } from '../components/ClusterFormulationsModal';
import { StatusBadge } from '../components/StatusBadge';
import { Card, EmptyState, ErrorBanner, Skeleton } from '../components/ui';
import { IconAlert, IconRefresh } from '../components/icons';
import { useTheme } from '../context/ThemeContext';
import { InventoryAlert } from '../types/inventory';
import { isAwaitingPayment, Order } from '../types/order';
import { ChartData, OverviewStats } from '../types/stats';
import { chartTheme, sliceRank } from '../utils/chartTheme';
import {
  capitalize,
  formatDate,
  formatDateShort,
  formatINR,
  formatINRCompact,
  shortId,
  timeAgo,
} from '../utils/format';

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card p-5">
      <p className="text-xs font-medium uppercase tracking-wide text-sub">{label}</p>
      <p className="mt-2 text-2xl font-semibold">{value}</p>
      {sub && <p className="mt-1 text-xs text-sub">{sub}</p>}
    </div>
  );
}

export default function DashboardPage() {
  const { isDark } = useTheme();
  const t = chartTheme(isDark);

  const [overview, setOverview] = useState<OverviewStats | null>(null);
  const [charts, setCharts] = useState<ChartData | null>(null);
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [alerts, setAlerts] = useState<InventoryAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [, setTick] = useState(0); // re-render "X mins ago" periodically

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const [ov, ch, orders, al] = await Promise.allSettled([
      statsApi.getOverview(),
      statsApi.getCharts(),
      ordersApi.getOrders({ page: 1, limit: 5 }),
      inventoryApi.getAlerts(),
    ]);
    if (ov.status === 'fulfilled') setOverview(ov.value);
    if (ch.status === 'fulfilled') setCharts(ch.value);
    if (orders.status === 'fulfilled') setRecentOrders(orders.value.items);
    if (al.status === 'fulfilled') setAlerts(al.value);
    if (ov.status === 'rejected' || ch.status === 'rejected') {
      setError(apiErrorMessage(ov.status === 'rejected' ? ov.reason : (ch as any).reason));
    }
    setLastUpdated(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const outOfStock = alerts.filter((a) => a.alert_type === 'out_of_stock');
  const lowStock = alerts.filter((a) => a.alert_type === 'low_stock');

  const pieData = charts
    ? [...(charts.orders_by_status ?? [])]
        .filter((d) => d.count > 0)
        .sort((a, b) => sliceRank(a.status) - sliceRank(b.status))
    : [];

  // Orders bottled and waiting on a courier — the dispatch queue. Read off the
  // status breakdown that already loads rather than on its own request; the card
  // is hidden entirely when the queue is empty, so it only ever appears as work
  // to do.
  const formulatedCount =
    charts?.orders_by_status?.find((d) => d.status === 'formulated')?.count ?? 0;

  // top_clusters arrives on the overview endpoint (AI service), not /charts
  const clusters =
    (charts?.top_clusters?.length ? charts.top_clusters : overview?.top_clusters) ?? [];

  // Which cluster's formulations are open, by label. Null = the dialog is shut.
  const [cluster, setCluster] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Dashboard</h1>
          <p className="text-sm text-sub">Last updated: {timeAgo(lastUpdated)}</p>
        </div>
        <button className="btn-outline" onClick={load} disabled={loading}>
          <IconRefresh className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}

      {/* Stat cards */}
      {loading && !overview ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : overview ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard
            label="Revenue this month (incl. GST)"
            value={formatINR(overview.revenue.this_month)}
            sub={`${formatINR(overview.revenue.today)} today`}
          />
          {/* Paid and not cancelled — abandoned checkouts are excluded, so this
              is smaller than the raw row count and is the number to trust. */}
          <StatCard
            label="Confirmed orders"
            value={String(overview.orders.confirmed_paid ?? 0)}
            sub={`${overview.orders.confirmed} paid & unfulfilled · ${overview.orders.pending} awaiting payment`}
          />
          <StatCard
            label="New users this week"
            value={String(overview.users.this_week)}
            sub={`${overview.users.total} total`}
          />
          <StatCard
            label="Formulations this month"
            value={String(overview.formulations.this_month)}
            sub={`${overview.formulations.total} total`}
          />
        </div>
      ) : null}

      {/* Dispatch queue — only shown when there is a batch waiting. */}
      {formulatedCount > 0 && (
        <Link
          to="/orders?status=formulated"
          className="card flex flex-wrap items-center justify-between gap-3 p-5 transition-colors hover:bg-edge/20"
        >
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-sub">Formulated</p>
            <p className="mt-2 text-2xl font-semibold">
              {formulatedCount} ready for dispatch
            </p>
          </div>
          <span className="text-sm font-medium text-gold">
            View &amp; print invoices &rarr;
          </span>
        </Link>
      )}

      {/* Charts */}
      {loading && !charts ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-72" />
          ))}
        </div>
      ) : charts ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card title="Revenue (incl. GST) — last 30 days">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={charts.daily_revenue} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={t.grid} strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDateShort}
                    tick={{ fill: t.axis, fontSize: 11 }}
                    stroke={t.grid}
                    tickLine={false}
                    minTickGap={24}
                  />
                  <YAxis
                    tickFormatter={(v: number) => formatINRCompact(v)}
                    tick={{ fill: t.axis, fontSize: 11 }}
                    stroke={t.grid}
                    tickLine={false}
                    axisLine={false}
                    width={64}
                  />
                  <Tooltip
                    contentStyle={t.tooltip}
                    labelFormatter={(l: any) => formatDate(String(l))}
                    formatter={(value: any) => [formatINR(Number(value)), 'Revenue']}
                    cursor={{ stroke: t.axis, strokeDasharray: '3 3' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke={t.gold}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: t.gold, stroke: t.surface, strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card title="Daily signups — last 30 days">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.daily_signups} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={t.grid} strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDateShort}
                    tick={{ fill: t.axis, fontSize: 11 }}
                    stroke={t.grid}
                    tickLine={false}
                    minTickGap={24}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: t.axis, fontSize: 11 }}
                    stroke={t.grid}
                    tickLine={false}
                    axisLine={false}
                    width={36}
                  />
                  <Tooltip
                    contentStyle={t.tooltip}
                    labelFormatter={(l: any) => formatDate(String(l))}
                    formatter={(value: any) => [String(value), 'Signups']}
                    cursor={{ fill: t.grid, opacity: 0.4 }}
                  />
                  <Bar dataKey="count" fill={t.gold} radius={[4, 4, 0, 0]} maxBarSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card title="Orders by status">
            <div className="h-64">
              {pieData.length === 0 ? (
                <EmptyState message="No orders yet" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="count"
                      nameKey="status"
                      innerRadius="52%"
                      outerRadius="80%"
                      paddingAngle={1}
                      stroke={t.surface}
                      strokeWidth={2}
                    >
                      {pieData.map((d) => (
                        <Cell key={d.status} fill={t.status[d.status] ?? t.axis} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={t.tooltip}
                      formatter={(value: any, name: any) => [String(value), capitalize(String(name))]}
                    />
                    <Legend
                      formatter={(value) => (
                        <span style={{ color: t.axis, fontSize: 12 }}>{capitalize(String(value))}</span>
                      )}
                      iconType="circle"
                      iconSize={8}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>

          <Card title="Top perfume clusters">
            <div className="h-64">
              {clusters.length === 0 ? (
                <EmptyState message="No formulations yet" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={clusters}
                    layout="vertical"
                    margin={{ top: 8, right: 16, left: 8, bottom: 0 }}
                    onClick={(e: any) => {
                      const label = e?.activePayload?.[0]?.payload?.cluster;
                      if (label) setCluster(String(label));
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <CartesianGrid stroke={t.grid} strokeDasharray="3 3" horizontal={false} />
                    <XAxis
                      type="number"
                      allowDecimals={false}
                      tick={{ fill: t.axis, fontSize: 11 }}
                      stroke={t.grid}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="cluster"
                      tick={{ fill: t.axis, fontSize: 11 }}
                      stroke={t.grid}
                      tickLine={false}
                      axisLine={false}
                      width={110}
                    />
                    <Tooltip
                      contentStyle={t.tooltip}
                      formatter={(value: any) => [String(value), 'Formulations']}
                      cursor={{ fill: t.grid, opacity: 0.4 }}
                    />
                    <Bar dataKey="count" fill={t.gold} radius={[0, 4, 4, 0]} maxBarSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* The bars themselves open the drill-down, but a chart bar is not
                keyboard reachable and does not announce that it is clickable —
                these chips are the same action, said plainly. */}
            {clusters.length > 0 && (
              <div className="mt-3 border-t border-edge pt-3">
                <p className="mb-2 text-xs text-sub">Open a cluster to see every formulation in it:</p>
                <div className="flex flex-wrap gap-2">
                  {clusters.map((c) => (
                    <button
                      key={c.cluster}
                      type="button"
                      onClick={() => setCluster(c.cluster)}
                      className="rounded-full border border-edge px-3 py-1 text-xs text-sub transition-colors hover:border-gold/50 hover:text-gold"
                    >
                      {c.cluster}
                      <span className="ml-1.5 tabular-nums text-sub/70">{c.count}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>
      ) : null}

      {/* Inventory alerts */}
      {outOfStock.length > 0 && (
        <div className="flex items-start gap-3 rounded-xl border border-bad/40 bg-bad/10 px-4 py-3 text-sm text-bad">
          <IconAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">
              {outOfStock.length} oil{outOfStock.length === 1 ? '' : 's'} out of stock
            </p>
            <p className="mt-0.5">
              {outOfStock.map((a) => a.oil_name).join(', ')} —{' '}
              <Link to="/inventory" className="font-semibold underline underline-offset-2">
                go to inventory
              </Link>
            </p>
          </div>
        </div>
      )}
      {lowStock.length > 0 && (
        <div className="flex items-start gap-3 rounded-xl border border-warn/40 bg-warn/10 px-4 py-3 text-sm text-warn">
          <IconAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Low stock: {lowStock.map((a) => a.oil_name).join(', ')} —{' '}
            <Link to="/inventory" className="font-semibold underline underline-offset-2">
              go to inventory
            </Link>
          </p>
        </div>
      )}

      {/* Recent orders */}
      <Card title="Recent orders" bodyClassName="!p-0">
        {loading && recentOrders.length === 0 ? (
          <div className="space-y-3 p-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : recentOrders.length === 0 ? (
          <EmptyState message="No orders yet" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-edge">
                  <th className="th">Order</th>
                  <th className="th">Date</th>
                  <th className="th">Amount</th>
                  <th className="th">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-edge">
                {recentOrders.map((o) => (
                  <tr key={o.id} className="transition-colors hover:bg-edge/20">
                    <td className="td">
                      <Link to={`/orders/${o.id}`} className="font-medium text-gold hover:underline">
                        {shortId(o.id)}
                      </Link>
                    </td>
                    <td className="td text-sub">{formatDate(o.created_at)}</td>
                    <td className="td font-medium">{formatINR(o.total)}</td>
                    <td className="td">
                      <StatusBadge
                        status={o.status}
                        label={isAwaitingPayment(o) ? 'Awaiting payment' : undefined}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {cluster && (
        <ClusterFormulationsModal cluster={cluster} onClose={() => setCluster(null)} />
      )}
    </div>
  );
}
